import { type ReactNode } from 'react';
import {
  Paragraph,
  Heading,
  Button,
  type ButtonStyleVariant as StyleVariant,
} from '@hungryroot/ramen';
import { SheetOrModal } from '../SheetOrModal/SheetOrModal';
import styles from './StyledSheetOrModal.module.css';

interface Props {
  testid?: string;
  onCancel: () => void;
  onAdd: () => void;
  bodyText?: string;
  heading: string;
  confirmButtonText: string;
  cancelButtonText?: string;
  ariaLabel: string;
  isOpen: boolean;
  callout?: string;
  className?: string;
  showXButton?: boolean;
  children?: ReactNode;
  clickOffSegmentEvent?: () => void;
  swipeCloseSegmentEvent?: () => void;
  isSubmitting?: boolean;
  contentClassName?: string;
  buttonsClassName?: string;
  buttonStyleVariant?: StyleVariant;
}

export function StyledSheetOrModal(props: Props) {
  const {
    children,
    testid,
    onCancel,
    onAdd,
    bodyText,
    heading,
    confirmButtonText,
    cancelButtonText,
    ariaLabel,
    isOpen = false,
    callout,
    className,
    showXButton,
    clickOffSegmentEvent,
    swipeCloseSegmentEvent,
    isSubmitting,
    contentClassName,
    buttonsClassName,
    buttonStyleVariant = 'default',
  } = props;

  return (
    <SheetOrModal
      data-testid={testid}
      onDismiss={onCancel}
      ariaLabel={ariaLabel}
      testid={`${testid}-modal`}
      isOpen={isOpen}
      contentClassName={`${styles.container} ${contentClassName}`}
      overlayClassName={styles.overlay}
      showXButton={showXButton}
      clickOffSegmentEvent={clickOffSegmentEvent}
      swipeCloseSegmentEvent={swipeCloseSegmentEvent}
    >
      <div className={className}>
        <Heading
          variant="H4"
          className={styles.heading}
          testid={`${testid}-heading`}
        >
          {heading}
        </Heading>
        {bodyText && (
          <Paragraph testid={`${testid}-body`}>{bodyText}</Paragraph>
        )}
        {children && children}
        {callout && (
          <Heading
            variant="H6"
            className={styles.callout}
            testid={`${testid}-callout`}
          >
            {callout}
          </Heading>
        )}
        <div className={`${styles.buttons} ${buttonsClassName}`}>
          <Button
            style={buttonStyleVariant}
            prominence="primary"
            size="medium"
            onClick={onAdd}
            loading={isSubmitting}
            textPrimary={confirmButtonText}
            className={styles.button}
            testid={`${testid}-confirm-button`}
          />
          {cancelButtonText && (
            <Button
              style="default"
              prominence="tertiary"
              size="medium"
              onClick={onCancel}
              textPrimary={cancelButtonText}
              className={styles.button}
              testid={`${testid}-cancel-button`}
            />
          )}
        </div>
      </div>
    </SheetOrModal>
  );
}
